import React, { useEffect, useRef, useState } from 'react';
import { measureString } from 'venn-utils';

const DEFAULT_MIN_WIDTH = 64;
const DEFAULT_EXTRA_WIDTH_BUFFER_FOR_CARET = 4;
const DEFAULT_FONT_SIZE = '14px';
const DEFAULT_FONT_WEIGHT = '400';
const DEFAULT_FONT_FAMILY = 'Roboto, sans-serif';

const DEFAULT_FONT_PROPERTIES = {
  fontSize: DEFAULT_FONT_SIZE,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontWeight: DEFAULT_FONT_WEIGHT,
};

interface AutosizeInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  minWidth?: number;
  extraWidth?: number;
}

interface FontProperties {
  fontSize: string;
  fontFamily: string;
  fontWeight: string | number;
}

function useAutosizeInput(
  value: string,
  fontProperties: FontProperties,
  minWidth = DEFAULT_MIN_WIDTH,
  extraWidth = DEFAULT_EXTRA_WIDTH_BUFFER_FOR_CARET,
  placeholder?: string,
): number {
  const [width, setWidth] = useState(minWidth);

  useEffect(() => {
    const textToMeasure = value || placeholder;

    const textWidth = measureString(textToMeasure || '', {
      fontSize: fontProperties.fontSize ?? DEFAULT_FONT_SIZE,
      fontFamily: fontProperties.fontFamily ?? DEFAULT_FONT_FAMILY,
      fontWeight: fontProperties.fontWeight ?? DEFAULT_FONT_WEIGHT,
    });
    const newWidth = Math.max(minWidth, textWidth + extraWidth);
    setWidth(newWidth);
  }, [value, fontProperties, minWidth, placeholder, extraWidth]);

  return width;
}

/**
 * This input will automatically resize to fit the text inside. This approach was implemented as a substitute for
 * the react-input-autosize library, which did not resize properly. It uses the Canvas API to measure the text width
 * instead of measuring hidden DOM elements.
 *
 * It also is much more lightweight, as it does not render any hidden elements.
 *
 * @param value The value of the input.
 * @param style The style of the input.
 * @param minWidth The minimum width of the input.
 * @param extraWidth The extra width to add to the input.
 * @param props The rest of the props.
 *
 * @constructor
 */
const AutosizeInput: React.FC<AutosizeInputProps> = ({
  value = '',
  style,
  minWidth = DEFAULT_MIN_WIDTH,
  extraWidth,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fontProperties, setFontProperties] = useState<FontProperties>(DEFAULT_FONT_PROPERTIES);

  useEffect(() => {
    if (inputRef.current) {
      const computed = getComputedStyle(inputRef.current);
      setFontProperties((({ fontFamily, fontSize, fontWeight }) => ({ fontFamily, fontSize, fontWeight }))(computed));
    }
  }, []);

  const width = useAutosizeInput(value.toString(), fontProperties, minWidth, extraWidth, props.placeholder);

  return (
    <input
      {...props}
      ref={inputRef}
      value={value}
      style={{
        ...style,
        width,
        minWidth,
        boxSizing: 'content-box',
      }}
    />
  );
};

export default AutosizeInput;
